import { ActionKey } from 'src/constants';
import {
  ActionPayload,
  ActionType,
  TriggerType,
} from 'src/interfaces/action.interface';

const BASE_ACTION_URL = 'https://api.integration.app';

const BaseActionPayload = {
  type: ActionType.External,
  action_trigger: {
    type: TriggerType.FunctionCall,
  },
};

type ActionConfigStub = {
  name: string;
  description: string;
  url?: string;
  input_schema: object;
  speak_on_send?: boolean;
  speak_on_receive?: boolean;
  signature_secret?: string;
  account_connection_id?: string;
};

const RetrieveAvailabilityActionStub: ActionConfigStub = {
  name: 'Retrieve Available Slots',
  description: 'A service to find meeting or appointment availability',
  input_schema: {
    type: 'object',
    properties: {
      endTime: {
        type: 'string',
        description:
          'End date time of the range to query for available slots. Example: 2025-02-05',
      },
      startTime: {
        type: 'string',
        description:
          'Start date time of the range to query for available slots. Example: 2025-02-01',
      },
      timeZone: {
        type: 'string',
        description:
          'Timezone identifier such as "America/Los_Angeles" or "America/New_York"',
      },
      eventTypeId: {
        type: 'string',
        description:
          'The calendar event type ID used to determine which type of event to look for',
      },
    },
    required: ['endTime', 'startTime', 'timeZone', 'eventTypeId'],
  },
  speak_on_send: false,
  speak_on_receive: false,
};

const BookAppointmentActionStub: ActionConfigStub = {
  name: 'Book Appointment',
  description: 'A service to book a meeting or an appointment',
  input_schema: {
    type: 'object',
    properties: {
      name: {
        type: 'string',
        description: 'Name of participant',
      },
      email: {
        type: 'string',
        description: 'Email address of participant',
      },
      start: {
        type: 'string',
        description:
          'Start date and time of meeting in ISO format. Example: 2025-03-01T10:00:00Z',
      },
      timeZone: {
        type: 'string',
        description:
          'Timezone identifier such as "America/Los_Angeles" or "America/New_York"',
      },
      eventTypeId: {
        type: 'string',
        description:
          'The calendar event type ID used to determine which type of event to look for',
      },
    },
    required: ['name', 'email', 'start', 'timeZone', 'eventTypeId'],
  },
  speak_on_send: false,
  speak_on_receive: false,
};

const actionConfigStubsByType: { [key: string]: ActionConfigStub } = {
  [ActionKey.RETRIEVE_AVAILABILITY]: RetrieveAvailabilityActionStub,
  [ActionKey.BOOK_APPOINTMENT]: BookAppointmentActionStub,
};

const getActionUrl = (connectionId: string, actionId: string) => {
  return `${BASE_ACTION_URL}/connections/${connectionId}/actions/${actionId}/run`;
};

export const generateActionStub = (
  type: ActionKey,
  connectionId: string,
  actionId: string,
  appName: string,
) => {
  return Object.assign({}, BaseActionPayload, {
    config: Object.assign({}, actionConfigStubsByType[type], {
      url: getActionUrl(connectionId, actionId),
    }),
    label: `${appName} - ${actionConfigStubsByType[type].name}`,
    description: actionConfigStubsByType[type].description,
  });
};

export const triggerTypes = [
  {
    label: 'Function Call',
    value: TriggerType.FunctionCall,
    description: 'Let Fluents.ai decide when to trigger this action',
  },
  {
    label: 'Phrase Based',
    value: TriggerType.PhraseBased,
    description: 'This action will trigger based on a bot-spoken phrase.',
  },
];

export const newAction: ActionPayload = {
  type: null as unknown as ActionType,
  config: {},
  action_trigger: {
    type: TriggerType.FunctionCall,
    config: {},
  },
  label: '',
};

export const actionConfig: {
  [key: string]: {
    icon: string;
    iconStyling: string;
    label: string;
    modalLabel: string;
    description: string;
    disabled?: boolean;
  };
} = {
  [ActionType.EndConversation]: {
    icon: 'end-conversation.svg',
    iconStyling: 'w-[22.4px] h-[8.7px]',
    label: 'End Conversation',
    modalLabel: 'End Conversation',
    description: 'Configure when the agent should end the call.',
  },
  [ActionType.TransferCall]: {
    icon: 'transfer-calls.svg',
    iconStyling: 'w-[24px] h-[24px]',
    label: 'Transfer Call',
    modalLabel: 'Transfer Calls',
    description:
      'Configure when the agent should transfer the call to another number.',
  },
  [ActionType.SetHold]: {
    icon: 'place-on-hold.svg',
    iconStyling: 'w-[12px] h-[14px]',
    label: 'Set Hold',
    modalLabel: 'Place on Hold',
    description:
      'This action places an active call on hold, pausing the conversation so neither party can hear each other until the call is resumed.',
    disabled: true,
  },
  [ActionType.AddToConference]: {
    icon: 'add-multiple-participants.svg',
    iconStyling: 'w-[21px] h-[20px]',
    label: 'Add To Conference',
    modalLabel: 'Add multiple participants',
    description:
      'This action helps add multiple participants to a call and merges individual calls into a single conference, allowing group communication.',
    disabled: true,
  },
  [ActionType.DTMF]: {
    icon: 'dtmf.svg',
    iconStyling: 'w-[25px] h-[25px]',
    label: 'DTMF',
    modalLabel: 'Use DTMF (Dual Tone Multi-frequency)',
    description: 'Allow the bot to send keypresses during a call.',
  },
  [ActionType.External]: {
    icon: 'custom-external-actions.svg',
    iconStyling: 'w-[18.12px] h-[18px]',
    label: 'External',
    modalLabel: 'Custom Input',
    description: 'Trigger your own APIs during a call.',
  },
  [ActionType.WarmTransfer]: {
    icon: 'add-multiple-participants.svg',
    iconStyling: 'w-[21px] h-[20px]',
    label: 'Warm Transfer Call',
    modalLabel: 'Warm Transfer Calls',
    description:
      'Have the agent contact the transfer recipient before transferring the call',
  },
};
